// src/pages/offers/components/OfferViewModal.jsx
import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

function StatCard({ label, value }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="rounded-xl border border-white/10 bg-[#1a2030] p-3 transition-all duration-200"
    >
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-lg font-semibold text-gray-100">{value}</div>
    </motion.div>
  );
}

function formatDateTime(dt) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    // Preferencia del usuario: formato europeo DD/MM/AAAA
    return d.toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dt);
  }
}

function Pill({ children, color = "rose" }) {
  const map = {
    rose: "bg-rose-500/15 text-rose-300 ring-1 ring-rose-400/30",
    emerald: "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30",
    blue: "bg-blue-500/15 text-blue-300 ring-1 ring-blue-400/30",
    slate: "bg-white/10 text-gray-200 ring-1 ring-white/15",
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${map[color] || map.slate}`}>
      {children}
    </span>
  );
}

export default function OfferViewModal({ open, onClose, offer }) {
  if (!open) return null;

  const isRejected = (offer?.status || "").toLowerCase() === "rejected";
  const client = offer?.client || {};
  const clientName = client?.name || offer?.client_name || "—";
  const clientCuit = client?.cuit || client?.tax_id || offer?.client_cuit || offer?.client_tax_id || "—";
  const clientEmail = client?.email || offer?.client_email || "—";
  const clientPhone = client?.phone || offer?.client_phone || "—";
  const clientAddress = [client?.address, client?.locality, client?.province].filter(Boolean).join(" · ");

  const ivaRate = useMemo(() => {
    const raw = offer?.iva_rate;
    if (raw == null) return 0.21;
    const n = Number(raw);
    return (n > 1 ? n / 100 : n) || 0.21;
  }, [offer]);

  const currency = useMemo(() => {
    const pc = (offer?.price_currency || "").toUpperCase();
    return pc.includes("USD") ? "USD" : "ARS";
  }, [offer]);

  const symbol = currency === "USD" ? "US$" : "AR$";
  const ocDocuments = useMemo(() => {
    const docs = Array.isArray(offer?.oc_documents)
      ? offer.oc_documents.filter(Boolean)
      : [];
    if (docs.length) return docs;
    if (offer?.oc_pdf_url) {
      return [
        {
          id: "legacy-oc",
          oc_number: offer?.oc_number || "",
          file_url: offer.oc_pdf_url,
          file_name: "Orden de compra",
          file_type: /\.pdf(\?.*)?$/i.test(offer.oc_pdf_url)
            ? "application/pdf"
            : "",
        },
      ];
    }
    return [];
  }, [offer]);

  const items = (offer?.services || []).slice();
  // Orden por position/order si existen; fallback por índice
  items.sort((a, b) => (a.position ?? a.order ?? 9999) - (b.position ?? b.order ?? 9999));

  const subtotal = items.reduce(
    (acc, it) => acc + Number(it.qty || 0) * Number(it.price || 0),
    0
  );
  const tax = Math.round(subtotal * ivaRate * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;
  const ivaLabel =
    ((ivaRate * 100) % 1 === 0 ? Math.round(ivaRate * 100) : (ivaRate * 100).toFixed(1)) + "";

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="w-full max-w-[min(1000px,100vw-2rem)] max-h-[96vh] mx-auto rounded-2xl bg-[#0f1115] text-gray-100 shadow-2xl border border-white/10 overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-[#12161d]">
              <div className="min-w-0 flex items-start gap-3">
                <span
                  className={`mt-1 inline-block h-3 w-3 rounded-full ${
                    isRejected
                      ? "bg-rose-500 shadow-[0_0_12px_rgba(244,63,94,.7)]"
                      : "bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,.8)]"
                  }`}
                />
                <div className="min-w-0">
                  <h3 className="text-xl font-semibold leading-none flex items-center gap-2">
                    {offer?.code ? `Oferta ${offer.code}` : "Oferta"}
                    {isRejected && <Pill color="rose">Rechazada</Pill>}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    Cliente: {clientName} · CUIT: {clientCuit}
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.96 }}
                className="rounded-lg px-2 py-1 text-lg hover:bg-white/10 transition-colors"
                onClick={onClose}
                type="button"
                aria-label="Cerrar"
              >
                ×
              </motion.button>
            </div>

            {/* Body con scroll vertical general */}
            <div className="max-h-[calc(96vh-60px-60px)] overflow-y-auto p-6 space-y-6">
              {/* Banner de rechazo (NUEVO) */}
              {isRejected && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.12] p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 text-rose-300">✗</div>
                    <div className="space-y-1">
                      <div className="text-sm font-semibold text-rose-200">
                        Oferta rechazada
                      </div>
                      <div className="text-sm text-rose-100">
                        <span className="text-rose-200">Motivo:</span>{" "}
                        {offer?.rejection_reason?.trim() || "—"}
                      </div>
                      <div className="text-xs text-rose-200/80">
  {offer?.rejected_at && <>Rechazada el {formatDateTime(offer.rejected_at)}</>}
</div>

                    </div>
                  </div>
                </div>
              )}

              {/* Totales */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard label="Subtotal" value={`${symbol}${subtotal.toFixed(2)}`} />
                <StatCard label={`IVA (${ivaLabel}%)`} value={`${symbol}${tax.toFixed(2)}`} />
                <StatCard label="Total" value={`${symbol}${total.toFixed(2)}`} />
              </div>

              {/* Datos de la oferta */}
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 bg-[#131923]">
                  <h4 className="text-sm font-semibold">Datos de la oferta</h4>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400">Tipo de servicio:</span>{" "}
                    <span>{offer?.service_type || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Certificación:</span>{" "}
                    <span>{offer?.certification || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Contacto (nombre):</span>{" "}
                    <span>{offer?.contact_name || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Contacto (email):</span>{" "}
                    <span>{offer?.contact_email || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Nº Cliente:</span>{" "}
                    <span>{client?.client_number || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Aceptación:</span>{" "}
                    <span>
                      {offer?.accepted_via
                        ? offer.accepted_via === "oc"
                          ? `OC${offer?.oc_number ? ` #${offer.oc_number}` : " pendiente de recepción"}`
                          : offer.accepted_via
                        : "—"}
                    </span>
                  </div>
                  {ocDocuments.length > 0 && (
                    <div className="sm:col-span-2">
                      <div className="text-gray-400 mb-2">Órdenes de compra:</div>
                      <div className="space-y-2">
                        {ocDocuments.map((doc, index) => (
                          <div
                            key={doc.id || `${index}`}
                            className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-[#11151c] px-3 py-2"
                          >
                            <span className="text-sm text-gray-200">
                              OC {doc.oc_number ? `#${doc.oc_number}` : "sin número"}
                            </span>
                            <span className="text-xs text-gray-500">
                              {doc.file_name || "Archivo adjunto"}
                            </span>
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-auto text-emerald-400 underline"
                            >
                              Ver archivo
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Datos del cliente */}
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 bg-[#131923]">
                  <h4 className="text-sm font-semibold">Datos del cliente</h4>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400">Nombre:</span>{" "}
                    <span>{clientName}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">CUIT / Tax ID:</span>{" "}
                    <span>{clientCuit}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Email:</span>{" "}
                    <span>{clientEmail}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Teléfono:</span>{" "}
                    <span>{clientPhone}</span>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-gray-400">Dirección:</span>{" "}
                    <span>{clientAddress || "—"}</span>
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              <div>
                <div className="text-sm font-medium mb-1">Observaciones</div>
                <div className="rounded-lg border border-white/10 bg-[#11151c] p-3 text-sm whitespace-pre-wrap text-gray-200">
                  {(offer?.notes || "")
                    .replace(/\[IVA:[^\]]+\]/gi, "")
                    .replace(/\[MONEDA:[^\]]+\]/gi, "")
                    .trim() || "—"}
                </div>
              </div>

              {/* Condiciones comerciales */}
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 bg-[#131923]">
                  <h4 className="text-sm font-semibold">Condiciones comerciales</h4>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400">Forma de pago:</span>{" "}
                    <span>{offer?.payment_method || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Moneda / Precio:</span>{" "}
                    <span>{offer?.price_currency || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Lugar de entrega:</span>{" "}
                    <span>{offer?.delivery_place || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Plazo de entrega:</span>{" "}
                    <span>{offer?.delivery_time || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Validez:</span>{" "}
                    <span>{offer?.validity_date || "—"}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">IVA:</span>{" "}
                    <span>{(offer?.iva_rate != null ? offer.iva_rate : 21) + "%"}</span>
                  </div>
                </div>
              </div>

              {/* Condiciones especiales */}
              {(offer?.special_conditions || "").trim() && (
                <div className="rounded-xl border border-white/10 overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/10 bg-[#131923]">
                    <h4 className="text-sm font-semibold">Condiciones especiales</h4>
                  </div>
                  <div className="p-4 text-sm whitespace-pre-wrap text-gray-200">
                    {offer.special_conditions}
                  </div>
                </div>
              )}

              {/* Servicios — Tabla con contenedor scrolleable y thead sticky */}
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 bg-[#131923]">
                  <h4 className="text-sm font-semibold">Servicios</h4>
                </div>

                <div className="p-0">
                  {/* Contenedor con scroll propio para la tabla */}
                  <div className="max-h-[50vh] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#1a2030] text-gray-300 border-b border-white/10 sticky top-0 z-10">
                        <tr>
                          <th className="p-3 text-left w-16">Ítem</th>
                          <th className="p-3 text-left">Descripción</th>
                          <th className="p-3 text-right w-36">PU</th>
                          <th className="p-3 text-right w-24">Cant.</th>
                          <th className="p-3 text-right w-36">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-4 text-gray-400">
                              No hay servicios cargados.
                            </td>
                          </tr>
                        )}

                        {items.map((it, idx) => {
                          const qty = Number(it.qty || 0);
                          const price = Number(it.price || 0);
                          const sub = qty * price;
                          const displayIndex = idx + 1; // SIEMPRE desde 1
                          return (
                            <motion.tr
                              key={it.id || idx}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.02 }}
                              className="border-b border-white/10 hover:bg-white/5"
                            >
                              <td className="p-3 text-left text-gray-300">{displayIndex}</td>
                              <td className="p-3 text-gray-100">{it.description || "—"}</td>
                              <td className="p-3 text-right font-medium text-gray-100">
                                {symbol}
                                {price.toFixed(2)}
                              </td>
                              <td className="p-3 text-right text-gray-300">{qty}</td>
                              <td className="p-3 text-right font-semibold text-emerald-300">
                                {symbol}
                                {sub.toFixed(2)}
                              </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end bg-[#12161d]">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-4 py-2 rounded-md border border-white/20 text-gray-100 hover:bg-white/10"
                onClick={onClose}
                type="button"
              >
                Cerrar
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
